import { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowClockwise, Package, PlusCircle, Truck, X } from 'shared/icons';
import { ExtractPaging } from '../../../utils/aggregation';
import StatusBadge from '../../StatusBadge';
import Badge from '../../ui/Badge';
import Button from '../../ui/Button';
import ConfirmModal from '../../ui/ConfirmModal';
import ErrorCard from '../../ui/ErrorCard';
import { DateTimeText, Id, Mono } from '../../ui/Mono';
import Select from '../../ui/Select';
import { SkeletonTable } from '../../ui/Skeleton';
import StandardTable from '../../ui/StandardTable';
import Textarea from '../../ui/Textarea';
import { useToast } from '../../ui/Toast';
import { ExtractListRows } from '../payload';
import OpenShipmentModal from './OpenShipmentModal';
import ShipmentChecklistPanel from './ShipmentChecklistPanel';
import ShipmentCloseModal from './ShipmentCloseModal';
import {
    FormatProgressText,
    ParseLpnCodeList,
    ParseShipmentProgress,
    SHIPMENT_STATUSES,
    ShipmentStatusVariant,
} from './shipmentModel';
import { AddShipmentItems, CancelShipment, GetShipment, ListShipments, RemoveShipmentItem } from './shippingApi';

const DEFAULT_PAGE_SIZE = 15;
const PAGE_SIZE_OPTIONS = [15, 30, 50, 100];
const BLANK_CELL = <span className="text-mono-400">—</span>;

/**
 * CONSOLE DE EXPEDIÇÃO (Supervisor, desktop) — lista de embarques em cima,
 * checklist do embarque escolhido embaixo.
 *
 * ── Por que o checklist ocupa a largura toda, e não uma coluna lateral ──────
 * O produto desta tela é a TRAVA DE 100%: o que falta bipar × o que já entrou
 * no veículo. Espremer isso numa coluna de 380 px empurraria a lista de
 * pendências para dentro de um scroll — exatamente a informação que precisa
 * estar visível sem esforço para o Supervisor decidir se manda fechar.
 *
 * ── A regra que a tela respeita ────────────────────────────────────────────
 * "Pode fechar?" é `can_close`, calculado pelo servidor. O botão desabilitado
 * NUNCA vem sozinho: os motivos (`close_blocks`) e a lista item a item ficam
 * acima dele o tempo todo. E mesmo com 100% na tela, o fechamento pode voltar
 * 409 — o servidor reconfere com os dados do banco. Esse caso tem tela própria
 * no `ShipmentCloseModal`, com o que falta e o caminho de volta.
 */
const ShippingWorkspace = ({ label, canManage }) => {
    const toast = useToast();
    const [rows, setRows] = useState([]);
    const [paging, setPaging] = useState({ total: 0, limit: DEFAULT_PAGE_SIZE, offset: 0 });
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [statusFilter, setStatusFilter] = useState('');
    const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
    const [loadError, setLoadError] = useState(null);

    const [selectedId, setSelectedId] = useState(null);
    const [detail, setDetail] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [detailError, setDetailError] = useState(null);

    const [openModal, setOpenModal] = useState(false);
    const [closeModal, setCloseModal] = useState(false);
    const [cancelModal, setCancelModal] = useState(false);
    const [cancelling, setCancelling] = useState(false);
    const [addLpnText, setAddLpnText] = useState('');
    const [addingLpns, setAddingLpns] = useState(false);
    const [removingItemId, setRemovingItemId] = useState(null);

    const loadList = useCallback(async () => {
        setLoadError(null);
        try {
            const offset = (Math.max(1, page) - 1) * Math.max(1, pageSize);
            const data = await ListShipments({ status: statusFilter || undefined, limit: pageSize, offset });
            const list = ExtractListRows(data, ['shipments']);
            setRows(list);
            setPaging(ExtractPaging(data, { total: list.length, limit: pageSize, offset }));
        } catch (error) {
            setLoadError(error?.message || label('wms.common.load_error'));
            setRows([]);
        } finally {
            setHasLoadedOnce(true);
        }
    }, [label, page, pageSize, statusFilter]);

    useEffect(() => {
        loadList();
    }, [loadList]);

    const loadDetail = useCallback(
        async (id) => {
            if (!id) {
                setDetail(null);
                return;
            }
            setDetailLoading(true);
            setDetailError(null);
            try {
                setDetail(await GetShipment(id));
            } catch (error) {
                setDetail(null);
                setDetailError(error?.message || label('wms.common.load_error'));
            } finally {
                setDetailLoading(false);
            }
        },
        [label],
    );

    useEffect(() => {
        loadDetail(selectedId);
    }, [loadDetail, selectedId]);

    /** Uma ação mexeu no embarque: a FICHA e a LISTA voltam do servidor juntas. */
    const refreshAll = useCallback(async () => {
        await Promise.all([loadDetail(selectedId), loadList()]);
    }, [loadDetail, loadList, selectedId]);

    const handleAddLpns = async () => {
        const codes = ParseLpnCodeList(addLpnText);
        if (codes.length === 0 || !detail?.id) {
            return;
        }
        setAddingLpns(true);
        try {
            const updated = await AddShipmentItems(detail.id, { lpn_codes: codes });
            setDetail(updated);
            setAddLpnText('');
            await loadList();
        } catch (error) {
            toast.error(error?.message || label('wms.common.load_error'));
        } finally {
            setAddingLpns(false);
        }
    };

    const handleRemoveItem = async (line) => {
        if (!detail?.id || !line?.id) {
            return;
        }
        setRemovingItemId(line.id);
        try {
            const updated = await RemoveShipmentItem(detail.id, line.id);
            setDetail(updated);
            await loadList();
        } catch (error) {
            toast.error(error?.message || label('wms.shipment.remove_item_loaded'));
        } finally {
            setRemovingItemId(null);
        }
    };

    const handleCancel = async () => {
        if (!detail?.id) {
            return;
        }
        setCancelling(true);
        try {
            const updated = await CancelShipment(detail.id);
            setDetail(updated);
            setCancelModal(false);
            await loadList();
        } catch (error) {
            toast.error(error?.message || label('wms.common.load_error'));
        } finally {
            setCancelling(false);
        }
    };

    const columns = useMemo(
        () => [
            {
                key: 'dock_code',
                label: label('wms.shipment.field.dock'),
                getValue: (row) => row.dock_code || '',
                render: (row) => <Id value={row.dock_code} className="font-medium" />,
            },
            {
                // O ROMANEIO é o contexto do embarque — placa, motorista e o
                // status do módulo legado, com o MESMO badge que a tela de
                // romaneio usa (utils/status.js), para os dois lugares não
                // pintarem "Coletado" de cores diferentes.
                key: 'outbound',
                label: label('wms.shipment.field.outbound'),
                getValue: (row) => row.outbound?.vehicle_plate || row.outbound_id || '',
                render: (row) => (
                    <span className="block space-y-1">
                        <Mono className="block text-xs text-mono-600 dark:text-mono-400">
                            {row.outbound?.vehicle_plate || '—'}
                        </Mono>
                        {row.outbound?.status ? (
                            <StatusBadge domain="wms" status={row.outbound.status} />
                        ) : (
                            BLANK_CELL
                        )}
                    </span>
                ),
            },
            {
                key: 'progress',
                label: label('wms.shipment.field.progress'),
                sortable: false,
                getValue: (row) => ParseShipmentProgress(row).percent,
                render: (row) => {
                    const progress = ParseShipmentProgress(row);
                    return (
                        <span className="flex items-center gap-2">
                            <Mono className="text-sm font-bold">{FormatProgressText(progress, label)}</Mono>
                            <Badge variant={progress.complete ? 'success' : 'warning'}>{`${progress.percent}%`}</Badge>
                        </span>
                    );
                },
            },
            {
                key: 'status',
                label: label('wms.shipment.field.status'),
                sortable: false,
                getValue: (row) => label(`wms.shipment_status.${row.status}`),
                render: (row) => (
                    <Badge variant={ShipmentStatusVariant(row.status)}>
                        {label(`wms.shipment_status.${row.status}`)}
                    </Badge>
                ),
            },
            {
                key: 'created_at',
                label: label('wms.shipment.field.created_at'),
                getValue: (row) => row.created_at || '',
                render: (row) => <DateTimeText value={row.created_at} relative />,
            },
        ],
        [label],
    );

    const listContent = !hasLoadedOnce ? (
        <SkeletonTable columns={6} rows={6} />
    ) : loadError ? (
        <ErrorCard
            title={label('wms.shipping.title')}
            message={loadError}
            action={label('wms.common.retry')}
            actionFn={loadList}
        />
    ) : (
        <StandardTable
            rows={rows}
            columns={columns}
            rowKey={(row, index) => row.id || index}
            onRowClick={(row) => setSelectedId(row.id)}
            countLabel={label('wms.shipment.count')}
            exportFileName="wms-embarques"
            emptyMessage={label('wms.shipment.empty')}
            // ⚠️ SEM campo de busca: a rota do M8 filtra por `outbound_id` e
            // `status`, e não tem `search`. Buscar no cliente com paginação de
            // SERVIDOR filtraria só a página carregada enquanto o rodapé
            // continuaria contando o total — meia verdade numa tela cujo assunto
            // é conferência. O filtro que existe de verdade está na barra.
            searchEnabled={false}
            paginationMode="server"
            totalItems={paging.total}
            currentPage={page}
            onPageChange={setPage}
            itemsPerPage={pageSize}
            onItemsPerPageChange={setPageSize}
            defaultPageSize={DEFAULT_PAGE_SIZE}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            toolbarLeft={
                <Select
                    value={statusFilter}
                    onChange={(event) => {
                        setStatusFilter(event.target.value);
                        setPage(1);
                    }}
                    className="w-44"
                >
                    <option value="">{label('wms.shipment.status_all')}</option>
                    {SHIPMENT_STATUSES.map((option) => (
                        <option key={option} value={option}>
                            {label(`wms.shipment_status.${option}`)}
                        </option>
                    ))}
                </Select>
            }
        />
    );

    const isOpen = detail?.status === 'OPEN';

    return (
        <div className="space-y-4">
            <div className="flex flex-wrap items-center justify-end gap-2">
                <Button variant="secondary" onClick={refreshAll}>
                    <ArrowClockwise size={16} className="mr-2" />
                    {label('wms.shipment.refresh')}
                </Button>
                {canManage ? (
                    <Button onClick={() => setOpenModal(true)}>
                        <PlusCircle size={18} className="mr-2" />
                        {label('wms.shipment.open_action')}
                    </Button>
                ) : null}
            </div>

            {listContent}

            {/* ── A FICHA: o checklist do embarque escolhido ───────────────── */}
            <section className="rounded-xl border border-mono-200 bg-white p-5 dark:border-mono-800 dark:bg-mono-950">
                {!selectedId ? (
                    <p className="py-6 text-center text-sm text-mono-500 dark:text-mono-400">
                        {label('wms.shipment.select_hint')}
                    </p>
                ) : detailLoading && !detail ? (
                    <SkeletonTable columns={2} rows={4} />
                ) : detailError ? (
                    <ErrorCard
                        title={label('wms.shipment.checklist_title')}
                        message={detailError}
                        action={label('wms.common.retry')}
                        actionFn={() => loadDetail(selectedId)}
                    />
                ) : detail ? (
                    <div className="space-y-5">
                        <header className="flex flex-wrap items-start justify-between gap-4 border-b border-mono-200 pb-4 dark:border-mono-800">
                            <div className="space-y-1">
                                <h3 className="flex items-center gap-2 text-lg font-bold text-black dark:text-white">
                                    <Truck size={20} className="shrink-0 text-mono-500" />
                                    {label('wms.shipment.checklist_title')}
                                    <Badge variant={ShipmentStatusVariant(detail.status)}>
                                        {label(`wms.shipment_status.${detail.status}`)}
                                    </Badge>
                                </h3>
                                <p className="text-sm text-mono-600 dark:text-mono-400">
                                    {label('wms.shipment.field.dock')}: <Id value={detail.dock_code} />
                                    {detail.outbound?.vehicle_plate ? (
                                        <>
                                            {' · '}
                                            {label('wms.shipment.field.vehicle_plate')}:{' '}
                                            <Mono>{detail.outbound.vehicle_plate}</Mono>
                                        </>
                                    ) : null}
                                    {detail.outbound?.driver_name ? ` · ${detail.outbound.driver_name}` : ''}
                                </p>
                                <p className="text-xs text-mono-500 dark:text-mono-400">
                                    {label('wms.shipment.field.created_at')}:{' '}
                                    <DateTimeText value={detail.created_at} />
                                    {detail.closed_at ? (
                                        <>
                                            {' · '}
                                            {label('wms.shipment.field.closed_at')}:{' '}
                                            <DateTimeText value={detail.closed_at} />
                                        </>
                                    ) : null}
                                </p>
                            </div>

                            <div className="flex flex-wrap items-center gap-2">
                                {canManage && isOpen ? (
                                    <>
                                        <Button variant="secondary" onClick={() => setCancelModal(true)}>
                                            <X size={16} className="mr-1.5" />
                                            {label('wms.shipment.cancel')}
                                        </Button>
                                        {/* O botão só habilita com `can_close` do
                                            SERVIDOR — e os motivos de não estar
                                            habilitado estão logo acima, no painel. */}
                                        <Button
                                            onClick={() => setCloseModal(true)}
                                            disabled={detail.can_close !== true}
                                            title={
                                                detail.can_close === true
                                                    ? undefined
                                                    : label('wms.shipment.close_blocked_title')
                                            }
                                        >
                                            <Package size={16} className="mr-1.5" />
                                            {label('wms.shipment.close')}
                                        </Button>
                                    </>
                                ) : null}
                            </div>
                        </header>

                        <p className="text-sm text-mono-500 dark:text-mono-400">
                            {label('wms.shipment.checklist_hint')}
                        </p>

                        <ShipmentChecklistPanel
                            shipment={detail}
                            label={label}
                            canManage={canManage}
                            onRemoveItem={handleRemoveItem}
                            removingItemId={removingItemId}
                        />

                        {canManage && isOpen ? (
                            <div className="space-y-2 rounded-xl border border-mono-200 p-4 dark:border-mono-800">
                                <Textarea
                                    label={label('wms.shipment.add_lpns')}
                                    value={addLpnText}
                                    onChange={(event) => setAddLpnText(event.target.value)}
                                    rows={2}
                                    placeholder={'REV-260812-001'}
                                    disabled={addingLpns}
                                />
                                <div className="flex items-center justify-between gap-3">
                                    <p className="text-xs text-mono-500 dark:text-mono-400">
                                        {label('wms.shipment.add_lpns_hint')}
                                    </p>
                                    <Button
                                        size="sm"
                                        variant="secondary"
                                        onClick={handleAddLpns}
                                        loading={addingLpns}
                                        disabled={ParseLpnCodeList(addLpnText).length === 0}
                                    >
                                        {label('wms.common.save')}
                                    </Button>
                                </div>
                            </div>
                        ) : null}

                        {detail.notes ? (
                            <p className="text-sm text-mono-600 dark:text-mono-400">
                                {label('wms.shipment.field.notes')}: {detail.notes}
                            </p>
                        ) : null}
                    </div>
                ) : null}
            </section>

            <OpenShipmentModal
                isOpen={openModal}
                onClose={() => setOpenModal(false)}
                label={label}
                onCreated={(created) => {
                    setOpenModal(false);
                    setSelectedId(created?.id ?? null);
                    setDetail(created ?? null);
                    loadList();
                }}
            />

            <ShipmentCloseModal
                isOpen={closeModal}
                onClose={() => setCloseModal(false)}
                shipment={detail}
                label={label}
                onClosed={(payload) => {
                    // A resposta do fechamento É a ficha do embarque já fechado
                    // (mais o resumo). Adotá-la evita a janela em que a tela
                    // ainda mostraria "Carregando" para algo que já saiu.
                    if (payload?.id) {
                        setDetail(payload);
                    }
                    loadList();
                }}
                // O 409 da trava pede exatamente isto: recarregar o checklist e
                // voltar para a doca. Nunca "tentar de novo" às cegas.
                onNeedsRefresh={refreshAll}
            />

            <ConfirmModal
                isOpen={cancelModal}
                onClose={() => setCancelModal(false)}
                onConfirm={handleCancel}
                loading={cancelling}
                title={label('wms.shipment.cancel_title')}
                message={label('wms.shipment.cancel_hint')}
                confirmText={label('wms.shipment.cancel_confirm')}
                cancelText={label('wms.common.cancel')}
                confirmVariant="danger"
            />
        </div>
    );
};

export default ShippingWorkspace;
